import { useCallback, useEffect, useState } from 'react';
import {
  useGetEncounterSignUrlFromStorageMutation,
  useGetFileUrlFromStorageMutation,
  useGetSignedUrlFromStorageByAdminMutation,
  useGetSignUrlFromStorageForRequesterLazyQuery,
} from 'graphql/generated/hasura';
import { useAuth } from '../auth/context/AuthContext';
import { getTokenFromFirebase } from '../firebase/hooks';

export const useGetSignedUrlFromStorage = (
  imageId: string | undefined,
  userId?: string | undefined,
): string | undefined => {
  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});
  const [getSignedUrlFromStorageByAdmin] =
    useGetSignUrlFromStorageForRequesterLazyQuery({});
  const [signedUrl, setSignedUrl] = useState<string>();

  useEffect(() => {
    if (imageId) {
      (async () => {
        let parsedImageId = imageId;
        if (parsedImageId.includes('http')) {
          parsedImageId = (parsedImageId.split('/').pop() || '').split('?')[0];
        }
        try {
          let resolvedUrl;
          if (userId) {
            const queryResult = await getSignedUrlFromStorageByAdmin({
              variables: { requestedUserId: userId, fileId: parsedImageId },
            });
            resolvedUrl =
              queryResult.data?.GetSignUrlFromStorageForRequester?.url;
          } else {
            const queryResult = await getFileUrlFromStorage({
              variables: { fileId: parsedImageId },
            });
            resolvedUrl = queryResult.data?.GetSignUrlFormStorage?.url;
          }
          setSignedUrl(resolvedUrl);
        } catch (e) {
          console.error(e);
          setSignedUrl(undefined);
        }
      })();
    }
  }, [getFileUrlFromStorage, getSignedUrlFromStorageByAdmin, imageId, userId]);

  return signedUrl;
};

export const useGetSignedUrlFromStorageFunction = (): ((
  imageId: string,
  userId?: string | undefined,
) => Promise<string | undefined>) => {
  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});
  const [getSignedUrlFromStorageByAdmin] =
    useGetSignedUrlFromStorageByAdminMutation({});

  const getSignedUrlFromStorage = useCallback(
    async (
      imageId: string,
      userId?: string | undefined,
    ): Promise<string | undefined> => {
      let parsedImageId = imageId;
      if (parsedImageId.includes('http')) {
        parsedImageId = parsedImageId.split('/').pop() || '';
      }
      let resolvedUrl;
      if (userId) {
        const queryResult = await getSignedUrlFromStorageByAdmin({
          variables: { userId: userId, fileId: parsedImageId },
        });
        resolvedUrl = queryResult.data?.GetSignUrlFromStorageByAdmin?.url;
      } else {
        const queryResult = await getFileUrlFromStorage({
          variables: { fileId: parsedImageId },
        });
        resolvedUrl = queryResult.data?.GetSignUrlFormStorage?.url;
      }
      return resolvedUrl;
    },
    [getFileUrlFromStorage, getSignedUrlFromStorageByAdmin],
  );
  return getSignedUrlFromStorage;
};

export const useGetEncounterSignedUrlFromStorageFunction = (): ((
  imageId: string,
  encounterId: string,
) => Promise<string | undefined>) => {
  const [getFileUrlFromStorage] = useGetEncounterSignUrlFromStorageMutation({});

  const getSignedUrlFromStorage = useCallback(
    async (
      imageId: string,
      encounterId: string,
    ): Promise<string | undefined> => {
      let parsedImageId = imageId;
      if (parsedImageId.includes('http')) {
        parsedImageId = parsedImageId.split('/').pop() || '';
      }

      const queryResult = await getFileUrlFromStorage({
        variables: { fileId: parsedImageId, encounterId: encounterId },
      });
      return queryResult.data?.GetEncounterSignUrlFromStorage?.url;
    },
    [getFileUrlFromStorage],
  );
  return getSignedUrlFromStorage;
};

export const useGetUrlFromStorage = (): ((
  fileId: string,
  userId?: string | undefined,
) => Promise<string | undefined>) => {
  const { user } = useAuth();
  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});
  const [getSignedUrlFromStorageByAdmin] =
    useGetSignUrlFromStorageForRequesterLazyQuery({});

  return useCallback(
    async (
      fileId: string,
      userId?: string | undefined,
    ): Promise<string | undefined> => {
      if (userId && userId !== user?.uuid) {
        const queryResult = await getSignedUrlFromStorageByAdmin({
          variables: { requestedUserId: userId, fileId: fileId },
        });
        return queryResult.data?.GetSignUrlFromStorageForRequester?.url;
      }
      const queryResult = await getFileUrlFromStorage({
        variables: { fileId: fileId },
      });
      return queryResult.data?.GetSignUrlFormStorage?.url;
    },
    [getFileUrlFromStorage, getSignedUrlFromStorageByAdmin, user?.uuid],
  );
};

export const useGetFileWithToken = () => {
  return useCallback(async (url: string) => {
    const authToken = await getTokenFromFirebase();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    return fetch(url, { headers }).then((response) => {
      if (!response.ok) {
        throw new Error('Network error');
      }
      return response.blob();
    });
  }, []);
};
